import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Printer, RefreshCw, Sparkles, Lock } from 'lucide-react';
import ScheduleDayBody from './ScheduleDayBody';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 20000;

const todayISO = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kuwait',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

// Public, token-gated view: a staff member opens their own link on their phone.
const StaffScheduleLinkView = () => {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const date = searchParams.get('date') || todayISO();

  const [day, setDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newIds, setNewIds] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [stale, setStale] = useState(false);
  const knownIds = useRef(null);
  const dayRef = useRef(null);

  const setDate = (next) => {
    const params = new URLSearchParams(searchParams);
    params.set('date', next);
    setSearchParams(params, { replace: true });
  };

  const shiftDate = (days) => {
    const d = new Date(`${date}T00:00:00`);
    d.setDate(d.getDate() + days);
    setDate(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    );
  };

  const fetchDay = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/staff-schedule/${token}`, { params: { date } });
      const ids = (res.data.appointments || []).map((a) => a.id);
      if (knownIds.current) {
        const fresh = ids.filter((id) => !knownIds.current.includes(id));
        if (fresh.length) {
          setNewIds((prev) => [...new Set([...prev, ...fresh])]);
          setTimeout(() => setNewIds((prev) => prev.filter((id) => !fresh.includes(id))), 60000);
        }
      }
      knownIds.current = ids;
      dayRef.current = res.data;
      setDay(res.data);
      setLastUpdated(new Date());
      setError('');
      setStale(false);
    } catch (e) {
      if (e.response?.status === 404) {
        // Link rotated or switched off while the page was open — stop showing stale data
        dayRef.current = null;
        setDay(null);
        setError(e.response?.data?.detail || 'This schedule link is no longer active');
        setStale(false);
      } else if (silent && dayRef.current) {
        setStale(true);
      } else {
        setError(e.response?.data?.detail || 'Could not load the schedule');
      }
    } finally {
      setLoading(false);
    }
  }, [token, date]);

  useEffect(() => {
    knownIds.current = null;
    dayRef.current = null;
    setNewIds([]);
    fetchDay();
  }, [fetchDay]);

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'visible') fetchDay(true);
    };
    const id = setInterval(tick, REFRESH_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [fetchDay]);

  return (
    <div className="min-h-screen bg-slate-50 pb-28 print:bg-white" data-testid="staff-share-schedule-page">
      <div className="mx-auto max-w-3xl px-4 pt-6">
        <header className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">My schedule</p>
              <h1 className="mt-1 truncate text-2xl font-bold text-slate-900" data-testid="share-staff-name">
                {day?.staff?.name || 'Schedule'}
              </h1>
              <p className="text-sm text-slate-500">
                {day?.staff?.role}
                {day?.provider?.name ? ` · ${day.provider.name}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-2 print:hidden">
              <button
                onClick={() => fetchDay(true)}
                data-testid="share-refresh-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-200"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Refresh
              </button>
              <button
                onClick={() => window.print()}
                data-testid="share-print-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-200"
              >
                <Printer className="h-3.5 w-3.5" /> Print
              </button>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button
              onClick={() => shiftDate(-1)}
              data-testid="share-prev-day-btn"
              className="grid h-10 w-10 place-items-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
            >
              <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
            </button>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value || todayISO())}
              data-testid="share-date-picker"
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-800"
            />
            <button
              onClick={() => shiftDate(1)}
              data-testid="share-next-day-btn"
              className="grid h-10 w-10 place-items-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
            >
              <ChevronRight className="h-4 w-4 rtl:rotate-180" />
            </button>
            {date !== todayISO() && (
              <button
                onClick={() => setDate(todayISO())}
                data-testid="share-today-btn"
                className="rounded-full bg-blue-600 px-4 py-2 text-xs font-bold text-white hover:bg-blue-700"
              >
                Today
              </button>
            )}
            {day?.is_today && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700"
                data-testid="share-today-badge"
              >
                <Sparkles className="h-3.5 w-3.5" /> Today
              </span>
            )}
          </div>

          <p className="mt-4 inline-flex items-center gap-1.5 text-xs text-slate-400 print:hidden">
            <Lock className="h-3 w-3" /> Private link — keep it to yourself
          </p>
        </header>

        <ScheduleDayBody day={day} loading={loading} error={error} newIds={newIds} />

        {lastUpdated && (
          <p className="mt-6 text-center text-xs text-slate-400 print:hidden" data-testid="share-last-updated">
            {stale ? (
              <span className="font-semibold text-amber-600">
                Can’t reach the server — showing the {lastUpdated.toLocaleTimeString()} version
              </span>
            ) : (
              <>Updated {lastUpdated.toLocaleTimeString()} · refreshes automatically</>
            )}
          </p>
        )}
      </div>
    </div>
  );
};

export default StaffScheduleLinkView;
